// @givmo/board — a carried motion's compensation details, on its card in the meeting's record
// (CBG-YR4-PAY; the part no frame drew, beside frame c1's dialog).
//
// One row per person whose pay the motion sets, in the order the officer added them (the contract's
// C2 as ruled: several per motion, each for one person). Each row says whose pay and how many
// documents it names; the details themselves open in the dialog that entered them. While the
// meeting's record is open a row can be changed or removed; once its minutes are approved the
// details stand, as everything else on the card does. The organization's Form 990 answer is a label
// for its return, not part of the record (the contract's C4), so the card doesn't show it: it is
// changed on Form 990 › Compensation decisions.

import type { BoardCompensationDecisionOut } from '@givmo/api-client';
import { RowActions } from '@givmo/design-system';
import { decisionFacts } from './compensation-decode';
import { compensationWords as w } from './compensation-words';

export interface MotionCompensationProps {
  motionId: string;
  decisions: readonly BoardCompensationDecisionOut[];
  /** Whether the person may change a decision's details (`board_meeting:write`, the record open). */
  canChangeDetails: boolean;
  onEdit: (decision: BoardCompensationDecisionOut) => void;
  onRemove: (decision: BoardCompensationDecisionOut) => void;
}

export function MotionCompensation({
  motionId,
  decisions,
  canChangeDetails,
  onEdit,
  onRemove,
}: MotionCompensationProps) {
  return (
    <div className="flex flex-col gap-6" data-testid={`motion-compensation-${motionId}`}>
      <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
        {w.title}
      </span>
      <ul className="flex flex-col gap-8">
        {decisions.map((decision) => {
          const facts = decisionFacts(decision);
          const actions =
            canChangeDetails && decision.editable
              ? [
                  {
                    label: w.edit,
                    testId: `motion-compensation-edit-${decision.id}`,
                    onSelect: () => onEdit(decision),
                  },
                  {
                    label: w.remove,
                    testId: `motion-compensation-remove-${decision.id}`,
                    destructive: true,
                    onSelect: () => onRemove(decision),
                  },
                ]
              : [];
          return (
            <li
              key={decision.id}
              className="flex items-start justify-between gap-8"
              data-testid={`motion-compensation-row-${decision.id}`}
            >
              <div className="flex min-w-0 flex-col gap-2">
                <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                  {decision.position
                    ? `${decision.person_name}, ${decision.position}`
                    : decision.person_name}
                </span>
                {facts !== null ? (
                  <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                    {facts}
                  </span>
                ) : null}
              </div>
              {actions.length > 0 ? (
                <RowActions
                  label={w.rowActions(decision.person_name)}
                  testId={`motion-compensation-actions-${decision.id}`}
                  actions={actions}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
